import React, { useState, useEffect } from 'react';
import { useMutation } from 'convex/react';
import { useLanguage } from '../../contexts/useLanguage';
import { User as UserType } from '../../types';
import { authenticateUser } from '../../services/userService';
import { CartItem } from '../../services/cartService';
import { computeCheckoutTotals } from './checkoutFormTotals';
import { persistCheckoutReservations } from './checkoutFormPersistReservations';
import { useCheckoutFormCountdown } from './useCheckoutFormCountdown';
import CheckoutFormSuccessView from './CheckoutFormSuccessView';
import CheckoutFormMainContent from './CheckoutFormMainContent';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { removeVumIncludedActivities } from '../../services/cartService';

interface CheckoutFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: CartItem[];
  shoppingCartData?: {
    startDate?: Date | null;
    endDate?: Date | null;
    groups?: Array<{ id: string; adults: number; kids: number }>;
  };
  paymentMode: 'guest' | 'member';
  currentUser?: UserType | null;
  onReservationSubmit: (reservationData: unknown) => void;
  onOpenAuth?: (mode: 'signin' | 'signup') => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  isOpen,
  onClose,
  selectedItems,
  shoppingCartData,
  paymentMode,
  currentUser,
  onReservationSubmit,
  onOpenAuth,
}) => {
  const { t } = useLanguage();
  const cartCtx = useWebDesignedCart();
  const vumPromo = cartCtx?.vumPromo ?? null;
  const [isLoggedIn, setIsLoggedIn] = useState(!!currentUser);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggedInUserId, setLoggedInUserId] = useState<string | null>(currentUser?.id || null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    hearAboutUs: '',
    paymentMethod: 'full',
    couponCode: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedReservationId, setSubmittedReservationId] = useState<string | null>(null);
  const [submittedReservationData, setSubmittedReservationData] = useState<{
    orderNumber?: string;
    customerName: string;
    totalAmount: string;
    items: string[];
  } | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const totals = computeCheckoutTotals(selectedItems, shoppingCartData, vumPromo);
  const { timeRemaining, isTimeLow, formatTime } = useCheckoutFormCountdown(isOpen, isSubmitted);

  const upsertGuest = useMutation(api.guests.upsert);
  const createBooking = useMutation(api.bookings.create);
  const markVumBooked = useMutation(api.vumCodes.markBooked);

  useEffect(() => {
    if (currentUser) {
      setIsLoggedIn(true);
      setLoggedInUserId(currentUser.id);
      setFormData((prev) => ({
        ...prev,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        phone: currentUser.phone || prev.phone,
      }));
    } else {
      setIsLoggedIn(false);
      setLoggedInUserId(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isOpen) {
      const autoFillFirstName = currentUser?.firstName || vumPromo?.guestFirstName || '';
      const autoFillLastName = currentUser?.lastName || vumPromo?.guestLastName || '';
      const autoFillEmail = currentUser?.email || '';
      const autoFillPhone = currentUser?.phone || '';

      setFormData({
        firstName: autoFillFirstName,
        lastName: autoFillLastName,
        email: autoFillEmail,
        phone: autoFillPhone,
        specialRequests: '',
        hearAboutUs: '',
        paymentMethod: 'full',
        couponCode: '',
      });

      setLoginData({ email: '', password: '' });
      setLoginError('');
      setIsSubmitted(false);
      setSubmittedReservationId(null);
      setSubmittedReservationData(null);
      setPaymentError(null);
    }
  }, [isOpen, currentUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    const user = authenticateUser(loginData.email, loginData.password);
    if (user) {
      setIsLoggedIn(true);
      setLoggedInUserId(user.id);
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || '',
      }));
      setLoginError('');
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: user }));
    } else {
      setLoginError(t('guestReservation.loginErrorInvalid'));
    }
  };

  const processAndSaveReservation = async (cloverChargeId?: string, cloverPaid?: boolean, cloverStatus?: string) => {
    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      const checkIn = shoppingCartData?.startDate
        ? shoppingCartData.startDate.toISOString().split('T')[0]
        : '';
      const checkOut = shoppingCartData?.endDate
        ? shoppingCartData.endDate.toISOString().split('T')[0]
        : '';
      const adults = shoppingCartData?.groups?.reduce((s, g) => s + g.adults, 0) ?? 1;
      const children = shoppingCartData?.groups?.reduce((s, g) => s + g.kids, 0) ?? 0;

      // A charge is considered successful when Clover provided a chargeId,
      // OR when Clover's own `paid` flag is true, OR when status is 'succeeded'.
      const chargeSucceeded = !!cloverChargeId || cloverPaid === true || cloverStatus === 'succeeded';

      // Create Convex bookings for all accommodation items
      for (const item of selectedItems) {
        if (item.type === 'accommodation' && checkIn && checkOut && formData.email) {
          try {
            const guestId = await upsertGuest({
              firstName: formData.firstName || 'Invité',
              lastName: formData.lastName || '',
              email: formData.email,
              phone: formData.phone || undefined,
              clerkUserId: isLoggedIn && loggedInUserId ? loggedInUserId : undefined,
            });

            // Apply VUM discount: included nights are $0, extra nights at normal rate
            let itemTotalPrice = item.itemPrice * totals.duration;
            if (vumPromo) {
              itemTotalPrice = item.itemPrice * totals.vumExtraNights;
            }

            // Full payment only (30% deposit path removed — aligned with quick reservation).
            const paymentMethod = 'full';
            const isPaid = chargeSucceeded;
            const paymentMethodStr = chargeSucceeded
              ? `${paymentMethod}:clover:${cloverChargeId ?? 'unknown'}`
              : paymentMethod;

            const bookingDepositAmount = chargeSucceeded ? totals.total : 0;
            const bookingBalanceAmount = chargeSucceeded ? 0 : totals.total;

            const bookingId = await createBooking({
              accommodationId: item.itemId as Id<'accommodations'>,
              guestId,
              checkIn,
              checkOut,
              guests: adults + children,
              adults,
              children,
              totalPrice: itemTotalPrice,
              paid: isPaid,
              status: chargeSucceeded ? 'confirmed' : 'pending',
              paymentMethod: paymentMethodStr,
              specialRequests: formData.specialRequests || undefined,
              hearAboutUs: formData.hearAboutUs || undefined,
              promoVum: vumPromo ? true : undefined,
              isGuest: !isLoggedIn || !loggedInUserId,
              depositAmount: bookingDepositAmount,
              balanceAmount: bookingBalanceAmount,
              gstAmount: totals.gst,
              qstAmount: totals.qst,
              taxeHebergementAmount: totals.taxeHebergement,
            });

            // Mark VUM code as booked on the first accommodation item
            if (vumPromo?.vumCodeId) {
              try {
                await markVumBooked({
                  id: vumPromo.vumCodeId as Id<'vumCodes'>,
                  bookingId,
                });
                cartCtx?.setVumPromo(null);
                removeVumIncludedActivities();
              } catch (err) {
                console.warn('Failed to mark VUM code as booked', err);
              }
            }
          } catch (err) {
            console.error('Failed to create Convex booking for item', item.itemName, err);
            throw err;
          }
        }
      }

      // Save locally for success view data
      const result = persistCheckoutReservations({
        selectedItems,
        shoppingCartData,
        formData,
        paymentMode,
        isLoggedIn,
        loggedInUserId,
        currentUser: currentUser ?? null,
        totals,
        cloverChargeId,
      });

      setIsSubmitted(true);
      setSubmittedReservationId(result.submittedReservationId ?? `checkout_${Date.now()}`);
      setSubmittedReservationData(result.submittedReservationData);
      onReservationSubmit({
        ...result.parentSubmitPayload,
        reservationId: result.submittedReservationId,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Une erreur est survenue. Veuillez réessayer.';
      setPaymentError(msg);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCloverPayment = async (chargeResult: { chargeId: string; amount: number; status: string; paid?: boolean }) => {
    if (!formData.hearAboutUs) {
      setPaymentError('Veuillez indiquer comment vous avez entendu parler de nous.');
      return;
    }
    if (totals.vumAnyCapacityExceeded) {
      setPaymentError(
        'Votre groupe dépasse les limites de ce forfait VUM. Veuillez nous contacter pour trouver une solution adaptée.'
      );
      return;
    }
    await processAndSaveReservation(chargeResult.chargeId, chargeResult.paid, chargeResult.status);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Both payment methods use Clover; payment is handled by the Clover form
  };

  // Show success view even after panel starts closing — keep it visible until user clicks close
  if (isSubmitted && submittedReservationId) {
    return (
      <CheckoutFormSuccessView
        t={t}
        submittedReservationId={submittedReservationId}
        submittedTotalAmount={submittedReservationData?.totalAmount}
        onClose={onClose}
      />
    );
  }

  if (!isOpen) return null;

  return (
      <CheckoutFormMainContent
        t={t}
        paymentMode={paymentMode}
        timeRemaining={timeRemaining}
        isTimeLow={isTimeLow}
        formatTime={formatTime}
        onClose={onClose}
        selectedItems={selectedItems}
        shoppingCartData={shoppingCartData}
        totals={totals}
        showPromoCouponField={!vumPromo}
        currentUser={currentUser}
      isLoggedIn={isLoggedIn}
      loginData={loginData}
      setLoginData={setLoginData}
      loginError={loginError}
      formData={formData}
      handleInputChange={handleInputChange}
      handleLogin={handleLogin}
      handleSubmit={handleSubmit}
      handleCloverPayment={handleCloverPayment}
      isProcessingPayment={isProcessingPayment}
      paymentError={paymentError}
      onOpenAuth={onOpenAuth}
    />
  );
};

export default CheckoutForm;
